import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ClipboardList, User, PencilLine, Building2, ArrowRight, ArrowLeft, Loader2, UserPlus, PhoneCall,
} from "lucide-react";

const STEPS = [
  { n: 1, label: "Overview", icon: ClipboardList },
  { n: 2, label: "Your Details", icon: User },
  { n: 3, label: "The Project", icon: PencilLine },
  { n: 4, label: "Business", icon: Building2 },
  { n: 5, label: "Callback", icon: PhoneCall },
  { n: 6, label: "Referral", icon: UserPlus },
];

const empty = {
  name: "", phone: "", email: "", what_needs_made: "", color_scheme: "",
  business_name: "", domain_name: "", has_domain: false, has_logo: false,
  referred_by_employee_id: "", callback_at: "", callback_window: "", callback_note: "",
};

export function ScheduleModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [employeeList, setEmployeeList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setForm((f) => ({
        ...empty,
        name: user ? user.name : "",
        email: user ? user.email : "",
      }));
      api.get("/employees/public").then(({ data }) => setEmployeeList(data)).catch(() => setEmployeeList([]));
    }
  }, [open, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 2) return form.name && form.email && form.phone;
    if (step === 3) return form.what_needs_made.trim().length > 3;
    if (step === 5) return !!form.phone && !!form.callback_at && !!form.callback_window;
    return true;
  };

  const submitBooking = async () => {
    setSubmitting(true);
    try {
      const noteParts = [];
      if (form.callback_window) noteParts.push(`Preferred window: ${form.callback_window}`);
      if (form.callback_note) noteParts.push(form.callback_note);
      const payload = {
        ...form,
        referred_by_employee_id: form.referred_by_employee_id || null,
        callback_note: noteParts.join(" — "),
      };
      await api.post("/bookings", payload);
      toast.success("Callback requested — we'll give you a call at your preferred time!");
      onOpenChange(false);
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const progress = (step / 6) * 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="schedule-modal"
        className="glass-panel max-w-xl gap-0 overflow-hidden rounded-2xl border-white/10 p-0 text-white sm:max-w-xl"
      >
        <DialogTitle className="sr-only">Request a Call Back</DialogTitle>
        <>
            <div className="h-1 w-full bg-white/5">
              <motion.div
                className="h-full bg-primary"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
            <div className="flex items-center justify-between px-6 pt-5">
              <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
                {STEPS.map((s) => (
                  <span key={s.n} className={s.n === step ? "text-primary" : ""}>
                    {s.n === step ? `0${s.n} · ${s.label}` : ""}
                  </span>
                ))}
              </div>
              <span className="font-mono text-xs text-zinc-500">Step {step}/6</span>
            </div>

            <div className="min-h-[320px] px-6 py-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }}
                >
                  {step === 1 && (
                    <div>
                      <h3 className="font-display text-2xl font-bold">Let's map your build.</h3>
                      <p className="mt-2 text-sm text-zinc-400">
                        This quick brief helps us understand your project before we call you.
                        Here's what we'll cover:
                      </p>
                      <ul className="mt-5 space-y-3">
                        {[
                          "Your contact details",
                          "What you need built",
                          "Business & domain info",
                          "Request a call back at your preferred time",
                        ].map((t, i) => (
                          <li key={i} className="flex items-start gap-3 border border-white/10 bg-white/[0.02] p-3">
                            <span className="font-mono text-primary">0{i + 1}</span>
                            <span className="text-sm text-zinc-200">{t}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-4 text-xs text-zinc-500">
                        Pricing and timeframe will be discussed on the call.
                      </p>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-4">
                      <h3 className="font-display text-2xl font-bold">Your details</h3>
                      <div>
                        <Label className="text-zinc-400">Full Name</Label>
                        <Input data-testid="booking-name" value={form.name} onChange={(e) => set("name", e.target.value)}
                          className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="Jane Doe" />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Phone Number</Label>
                        <Input data-testid="booking-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                          className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="+1 555 000 0000" />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Email</Label>
                        <Input data-testid="booking-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                          disabled={!!user}
                          className="mt-1.5 rounded-sm border-white/10 bg-black disabled:opacity-60" placeholder="jane@company.com" />
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-4">
                      <h3 className="font-display text-2xl font-bold">What needs to be made?</h3>
                      <p className="text-sm text-zinc-400">Describe your project, goals, features and any references.</p>
                      <Textarea data-testid="booking-brief" rows={5} value={form.what_needs_made}
                        onChange={(e) => set("what_needs_made", e.target.value)}
                        className="rounded-sm border-white/10 bg-black"
                        placeholder="I need a fast custom storefront for my coffee brand with product pages and checkout..." />
                      <div>
                        <Label className="text-zinc-400">Main colors / color scheme</Label>
                        <Input data-testid="booking-colors" value={form.color_scheme}
                          onChange={(e) => set("color_scheme", e.target.value)}
                          className="mt-1.5 rounded-sm border-white/10 bg-black"
                          placeholder="e.g. black & electric blue, warm earthy tones…" />
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="space-y-4">
                      <h3 className="font-display text-2xl font-bold">Business & domain</h3>
                      <div>
                        <Label className="text-zinc-400">Business Name</Label>
                        <Input data-testid="booking-business" value={form.business_name} onChange={(e) => set("business_name", e.target.value)}
                          className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="Acme Coffee Co." />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Do you already have a domain name?</Label>
                        <div className="mt-2 flex gap-2">
                          <Button type="button" data-testid="booking-has-domain-yes"
                            onClick={() => set("has_domain", true)}
                            className={`rounded-full ${form.has_domain ? "bg-primary text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
                            Yes
                          </Button>
                          <Button type="button" data-testid="booking-has-domain-no"
                            onClick={() => { set("has_domain", false); set("domain_name", ""); }}
                            className={`rounded-full ${!form.has_domain ? "bg-primary text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
                            Not yet
                          </Button>
                        </div>
                      </div>
                      {form.has_domain && (
                        <div>
                          <Label className="text-zinc-400">Domain Name</Label>
                          <Input data-testid="booking-domain" value={form.domain_name} onChange={(e) => set("domain_name", e.target.value)}
                            className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="acmecoffee.com" />
                        </div>
                      )}
                      <div>
                        <Label className="text-zinc-400">Do you have a logo?</Label>
                        <div className="mt-2 flex gap-2">
                          <Button type="button" data-testid="booking-has-logo-yes"
                            onClick={() => set("has_logo", true)}
                            className={`rounded-full ${form.has_logo ? "bg-primary text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
                            Yes
                          </Button>
                          <Button type="button" data-testid="booking-has-logo-no"
                            onClick={() => set("has_logo", false)}
                            className={`rounded-full ${!form.has_logo ? "bg-primary text-white" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
                            Not yet
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 5 && (
                    <div className="space-y-4">
                      <h3 className="font-display text-2xl font-bold">Request a call back</h3>
                      <p className="text-sm text-zinc-400">No meetings to schedule — we'll simply call you. Tell us the best number and when to reach you.</p>
                      <div>
                        <Label className="text-zinc-400">Phone number</Label>
                        <Input data-testid="callback-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                          className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="+1 555 000 0000" />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Preferred date</Label>
                        <Input data-testid="callback-date" type="date" min={new Date().toISOString().split("T")[0]}
                          value={form.callback_at} onChange={(e) => set("callback_at", e.target.value)}
                          className="mt-1.5 rounded-sm border-white/10 bg-black" />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Preferred time window</Label>
                        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                          {["Morning", "Afternoon", "Evening", "Anytime"].map((w) => (
                            <button key={w} type="button" data-testid={`callback-window-${w.toLowerCase()}`}
                              onClick={() => set("callback_window", w)}
                              className={`border p-2.5 text-sm transition-colors ${form.callback_window === w ? "border-primary bg-primary/10 text-white" : "border-white/10 bg-black text-zinc-300 hover:border-white/25"}`}>
                              {w}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-zinc-400">Anything else? (optional)</Label>
                        <Textarea data-testid="callback-note" rows={2} value={form.callback_note}
                          onChange={(e) => set("callback_note", e.target.value)}
                          className="mt-1.5 rounded-sm border-white/10 bg-black"
                          placeholder="e.g. weekdays only, after 5pm is best…" />
                      </div>
                    </div>
                  )}

                  {step === 6 && (
                    <div className="space-y-4">
                      <h3 className="font-display text-2xl font-bold">Who referred you?</h3>
                      <p className="text-sm text-zinc-400">If one of our team members sent you our way, pick them below.</p>
                      <Select value={form.referred_by_employee_id || "none"}
                        onValueChange={(v) => set("referred_by_employee_id", v === "none" ? "" : v)}>
                        <SelectTrigger data-testid="booking-referral-select" className="rounded-sm border-white/10 bg-black">
                          <SelectValue placeholder="Select who referred you" />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-[#0A0A0B] text-white">
                          <SelectItem value="none" data-testid="booking-referral-none">No one / not referred</SelectItem>
                          {employeeList.map((emp) => (
                            <SelectItem key={emp.id} value={emp.id} data-testid={`booking-referral-${emp.id}`}>{emp.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {employeeList.length === 0 && (
                        <p className="text-xs text-zinc-500">No referrers available — just continue.</p>
                      )}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex items-center justify-between border-t border-white/10 px-6 py-4">
              <Button
                data-testid="booking-back-btn"
                variant="ghost"
                disabled={step === 1}
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                className="rounded-full text-zinc-400 hover:text-white disabled:opacity-30"
              >
                <ArrowLeft className="mr-1 h-4 w-4" /> Back
              </Button>
              {step < 6 ? (
                <Button
                  data-testid="booking-next-btn"
                  disabled={!canNext()}
                  onClick={() => setStep((s) => s + 1)}
                  className="rounded-full bg-primary px-6 text-white hover:bg-primary/90 disabled:opacity-40"
                >
                  Continue <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  data-testid="booking-submit-btn"
                  disabled={submitting}
                  onClick={submitBooking}
                  className="rounded-full bg-primary px-6 text-white hover:bg-primary/90 disabled:opacity-40"
                >
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Request Call Back
                </Button>
              )}
            </div>
          </>
      </DialogContent>
    </Dialog>
  );
}
